/**
 * Soort pagina per URL — GA4's "content grouping".
 *
 * Waarom. De site heeft ruim 2.700 adressen: 2.384 artikelpagina's, 169 groeppagina's, 57
 * kennisbankartikelen, 27 stadspagina's en de rest. Een padverkenning op het kále pad levert
 * daardoor een waaier van duizenden takjes op waar geen patroon in te zien is. Met een groep
 * erbij wordt dezelfde verkenning leesbaar:
 *
 *   Landingspagina → Configurator → Winkelwagen → Afrekenen → Aankoop
 *              ↘ Kennisbank → Configurator
 *
 * Dát is de vraag die Thijs stelde: komen mensen binnen op een kennisbankartikel en gaan ze door
 * naar een configurator, of lopen ze daar dood? Verkopen de stadspagina's iets?
 *
 * Let op: GA4 past content groups NIET met terugwerkende kracht toe. Wat vanaf vandaag gemeten
 * wordt, is ook pas vanaf vandaag te zien.
 *
 * De groepen zijn bewust grof. Fijner betekent weer duizenden takjes; grover ("Winkel" vs "Rest")
 * verbergt juist de overgang die je wilt zien. Deze indeling volgt de stappen die een klant zet.
 */

/** De vaste namen. Eén bron, zodat een typefout niet stilletjes een dertiende groep maakt. */
export const CONTENT_GROUPS = [
  "Homepage",
  "Landingspagina",
  "Categorie",
  "Productpagina",
  "Configurator",
  "Rekentools",
  "Winkelwagen",
  "Afrekenen",
  "Kennisbank",
  "Stadspagina",
  "Zoeken",
  "Account",
  "Service",
  "Overig",
] as const;
export type ContentGroup = (typeof CONTENT_GROUPS)[number];

/** Landingspagina's voor glas die niet onder /categorie/<één segment> vallen. */
const LOSSE_LANDINGS = new Set(["/glas-op-maat", "/glassoorten", "/vacuumglas"]);

/** Paden die bij het afrekenen horen (de laatste stap vóór de aankoop). */
// /betalen = de pagina achter de betaallink in onze mails; hoort bij dezelfde stap.
const AFREKENEN = new Set(["/afrekenen", "/betaling", "/betalen"]);

/** Service- en informatiepagina's: geen stap in de koopweg, wel bezocht. */
const SERVICE = new Set([
  "/contact", "/levertijden", "/garantie", "/herroepingsrecht", "/herroepen", "/retour",
  "/veelgestelde-vragen", "/over-ons", "/zakelijk", "/glas-laten-plaatsen", "/reviews",
  "/privacybeleid", "/privacy", "/cookies", "/algemene-voorwaarden", "/nieuwsbrief/afmelden",
  "/afmelden", "/uitschrijven", "/api-glasprijs",
]);

const REKENTOOLS = new Set(["/u-waarde-calculator", "/glasgewicht-calculator"]);

/**
 * Bepaalt de groep uit het pad. De query-string gaat eraf: `?kleur=chinchilla` maakt geen ander
 * soort pagina, en zou anders elke variant een eigen regel geven — precies het probleem dat deze
 * indeling oplost.
 */
export function contentGroup(padMetQuery: string): ContentGroup {
  const pad = (padMetQuery.split("?")[0] ?? "").replace(/\/+$/, "") || "/";
  // De Engelse site draait op dezelfde routes onder /en; de groep is dezelfde.
  const p = pad.startsWith("/en/") ? pad.slice(3) : pad === "/en" ? "/" : pad;
  const delen = p.split("/").filter(Boolean);

  if (p === "/") return "Homepage";
  if (LOSSE_LANDINGS.has(p)) return "Landingspagina";
  if (AFREKENEN.has(p)) return "Afrekenen";
  if (SERVICE.has(p)) return "Service";
  if (REKENTOOLS.has(p)) return "Rekentools";
  if (p === "/winkelwagen") return "Winkelwagen";
  if (p === "/zoeken") return "Zoeken";
  // De actiepagina is een productoverzicht, geen glas-landing: hem bij de landingspagina's zetten
  // zou de instroom op glas vertroebelen met een lijst afgeprijsde artikelen.
  if (p === "/acties") return "Categorie";

  // Samenstellen/configureren, in welke vorm dan ook.
  if (p.endsWith("/samenstellen") || p === "/douche/opstellingen") return "Configurator";
  if (delen[0] === "glas-op-maat" && delen.length > 1) return "Configurator";
  if (delen[0] === "vacuumglas" && delen.length > 1) return "Configurator";

  if (delen[0] === "artikel") return "Productpagina";
  if (delen[0] === "kennisbank") return "Kennisbank";
  if (delen[0] === "glas-bezorgen") return "Stadspagina";
  if (delen[0] === "account" || ["inloggen", "registreren", "wachtwoord-vergeten"].includes(delen[0] ?? "")) return "Account";

  // /categorie/<afdeling> is een landingspagina; alles dieper is een groep- of subgroeppagina.
  if (delen[0] === "categorie") return delen.length === 1 ? "Overig" : delen.length === 2 ? "Landingspagina" : "Categorie";

  return "Overig";
}
